"use client";

import React from "react";
import { MapPin, Phone, Mail, MessageCircle, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="224468" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-chart-1/20 rounded-none flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-chart-1" />
                </div>
                <h3 className="text-2xl font-default font-bold text-foreground uppercase tracking-wider">Transmission Received</h3>
                <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-8 rounded-none border-chart-1 text-chart-1 hover:bg-chart-1 hover:text-primary-foreground">
                    Send another inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Full Name <span className="text-chart-1">*</span></FormLabel>
                            <FormControl>
                                <Input placeholder="Miles Henshaw" className="bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                            </FormControl>
                            <FormDescription className="font-mono text-[10px] uppercase opacity-50">State your identity.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Email Address <span className="text-chart-1">*</span></FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="miles@example.com" className="bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                            </FormControl>
                            <FormDescription className="font-mono text-[10px] uppercase opacity-50">Where can I reach you?</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\s-]{8,20}$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+44 1482 000000" className="bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                            </FormControl>
                            <FormDescription className="font-mono text-[10px] uppercase opacity-50">Optional – for direct communication.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Artist or Band Name</FormLabel>
                            <FormControl>
                                <Input placeholder="The Experimentalists" className="bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                            </FormControl>
                            <FormDescription className="font-mono text-[10px] uppercase opacity-50">Optional – who are we producing?</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="packaging_needs" rules={{
      required: "Please describe your vision"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Project Details & Sonic Vision <span className="text-chart-1">*</span></FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe your sound, influences, and specific needs (recording, mixing, live sound, etc.)" className="min-h-[120px] resize-y bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                        </FormControl>
                        <FormDescription className="font-mono text-[10px] uppercase opacity-50">Detail your raw energy and authenticity.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="quantity" rules={{
      min: {
        value: 1,
        message: "Minimum 1 track required"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Number of Tracks</FormLabel>
                        <FormControl>
                            <Input type="number" placeholder="Ex. 3" min="1" className="bg-muted/30 border-border focus:border-chart-1 transition-colors rounded-none font-sans" {...field} />
                        </FormControl>
                        <FormDescription className="font-mono text-[10px] uppercase opacity-50">Estimated track count for the session.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3">
                    <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive font-mono uppercase tracking-tight">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-default font-bold uppercase tracking-widest bg-chart-1 hover:bg-chart-1/90 text-primary-foreground rounded-none shadow-none transition-all duration-300">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Transmitting...
                    </> : 'Initiate Collaboration'}
            </Button>
        </Form>;
}
export default function ParlonsDeVosBesoins() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftColRef,
    isVisible: isLeftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: isRightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="lets-collaborate" className="bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-none bg-chart-1/5 blur-[120px]" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-none bg-primary/5 blur-[120px]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-700 transform ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter uppercase">
                        Let's Collaborate
                    </h2>
                    <p className="text-lg md:text-xl text-muted-foreground font-sans">Ready to refine your sound?</p>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {}
                    <div ref={leftColRef} className={`lg:col-span-5 space-y-10 transition-all duration-700 delay-100 transform ${isLeftColVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                        <div>
                            <h3 className="text-2xl font-default font-bold text-foreground mb-6 uppercase tracking-tight">
                                Contact Information
                            </h3>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4 group">
                                    
                                    <div>
                                        <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Base</h4>
                                        <p className="text-foreground leading-relaxed font-sans font-medium">Hull<br />United Kingdom</p>
                                    </div>
                                </div>

                                

                                <div className="flex items-start gap-4 group">
                                    <div className="mt-1 size-12 rounded-none bg-muted/50 flex items-center justify-center shrink-0 group-hover:bg-chart-1/20 transition-colors duration-300">
                                        <Mail className="size-6 text-chart-1" />
                                    </div>
                                    <div>
                                        <h4 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Electronic</h4>
                                        <p className="text-foreground leading-relaxed font-sans font-medium">miles@iammileshen.com</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4">
                            <Button variant="outline" className="w-full sm:w-auto h-12 text-base rounded-none border-chart-1 text-chart-1 hover:bg-chart-1 hover:text-primary-foreground transition-colors gap-2 font-mono uppercase tracking-widest" asChild>
                                <Link href="mailto:miles@iammileshen.com">Get in Touch</Link>
                            </Button>
                        </div>

                        {}
                        <div className="rounded-none overflow-hidden border border-border shadow-none h-[300px] relative bg-muted/20">
                            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d75294.01956184517!2d-0.3456345!3d53.7456709!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d6205808796f7d%3A0x9599589d9703f831!2sHull!5e0!3m2!1sen!2suk!4v1710000000000!5m2!1sen!2suk" width="100%" height="100%" style={{
              border: 0
            }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full grayscale hover:grayscale-0 transition-all duration-700 opacity-70 hover:opacity-100" title="Studio Location"></iframe>
                        </div>
                    </div>

                    {}
                    <div ref={rightColRef} className={`lg:col-span-7 transition-all duration-700 delay-200 transform ${isRightColVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                        <Card className="border-border/50 shadow-none rounded-none bg-card/40 backdrop-blur-md">
                            <CardHeader className="pb-6 border-b border-border/50">
                                <CardTitle className="text-2xl font-default font-bold text-foreground uppercase tracking-tight">Project Brief</CardTitle>
                                <CardDescription className="text-base font-sans">Outline your creative goals below. and ill respond</CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8">
                                <FormProvider formKey="collaboration_form_get_in_touch" formId="collaboration_form_get_in_touch" sectionName="ParlonsDeVosBesoins" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                packaging_needs: "",
                quantity: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  pattern: "phone"
                },
                packaging_needs: {
                  required: true
                },
                quantity: {
                  min: 1
                }
              }} submitText="Initiate Collaboration" successMessage="Transmission successful. I'll be in touch shortly to discuss the raw energy and direction of your project.">
                                    <ContactForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>;
}